import { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Wrench, Camera, Video, Plus, Search, CheckCircle, Clock, Eye, Upload, X, Play } from 'lucide-react';

function MaintenanceLog() {
  const { API } = useContext(AppContext);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingLog, setUploadingLog] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('image'); // 'image' or 'video'
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchLogs();
  }, [statusFilter]);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/maintenance/logs`, {
        params: { status: statusFilter },
        
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const openUploadModal = (log, type) => {
    setUploadingLog(log);
    setUploadType(type);
    setShowUploadModal(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'maintenance');
    formData.append('reference_id', uploadingLog.id);

    setUploading(true);
    try {
      const endpoint = uploadType === 'video' ? '/upload/video' : '/upload/image';
      const response = await axios.post(`${API}${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        
      });
      
      alert(`${uploadType === 'video' ? 'Video' : 'Photo'} uploaded successfully!`);
      setShowUploadModal(false);
      fetchLogs();
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Preventive': return 'bg-blue-100 text-blue-700';
      case 'Breakdown': return 'bg-red-100 text-red-700';
      case 'Corrective': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-700';
      case 'In Progress': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getShiftColor = (shift) => {
    switch (shift) {
      case 'Day': return 'bg-amber-100 text-amber-700';
      case 'Night': return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const stats = {
    total: logs.length,
    completed: logs.filter(l => l.status === 'Completed').length,
    inProgress: logs.filter(l => l.status === 'In Progress').length,
    totalCost: logs.reduce((sum, l) => sum + (l.cost || 0), 0),
  };

  if (loading) {
    return <div className="text-center py-12">Loading maintenance logs...</div>;
  }

  return (
    <div className="space-y-6" data-testid="maintenance-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Maintenance Log</h1>
          <p className="text-gray-500 mt-1">Track all maintenance activities with photo/video proof</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md"
        >
          <Plus size={18} />
          <span>Log Maintenance</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-2">
            <Wrench className="text-[#1a237e]" size={18} />
            <p className="text-sm text-gray-500">Total Entries</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center space-x-2">
            <CheckCircle className="text-green-500" size={18} />
            <p className="text-sm text-green-600 font-medium">Completed</p>
          </div>
          <p className="text-2xl font-bold text-green-700">{stats.completed}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-5 border border-yellow-200">
          <div className="flex items-center space-x-2">
            <Clock className="text-yellow-600" size={18} />
            <p className="text-sm text-yellow-700 font-medium">In Progress</p>
          </div>
          <p className="text-2xl font-bold text-yellow-700">{stats.inProgress}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Total Cost</p>
          <p className="text-2xl font-bold text-gray-900">₹{(stats.totalCost / 1000).toFixed(1)}K</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2 bg-white rounded-xl p-1.5 border border-gray-200">
          {['all', 'Completed', 'In Progress'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                statusFilter === status
                  ? 'bg-gradient-to-r from-[#1a237e] to-[#283593] text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {status === 'all' ? 'All' : status}
            </button>
          ))}
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Log ID</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Machine</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Technician</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Shift</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Duration</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Cost</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Media</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 font-mono text-sm text-[#1a237e] font-medium">{log.id}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{log.date}</td>
                  <td className="px-4 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{log.machineName}</p>
                      <p className="text-xs text-gray-500 font-mono">{log.machineId}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getTypeColor(log.type)}`}>
                      {log.type}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600 max-w-xs truncate">{log.description}</td>
                  <td className="px-4 py-4 text-sm text-gray-900">{log.technician}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getShiftColor(log.shift)}`}>
                      {log.shift}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">{log.duration} {log.unit}</td>
                  <td className="px-4 py-4 text-sm font-semibold text-gray-900">₹{log.cost?.toLocaleString()}</td>
                  <td className="px-4 py-4">
                    {log.hasMedia ? (
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => openUploadModal(log, 'image')}
                          className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200"
                          title="View/Add Photos"
                        >
                          <Eye size={14} />
                        </button>
                        <button 
                          onClick={() => openUploadModal(log, 'image')}
                          className="p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                          title="Add Photo"
                        >
                          <Camera size={14} />
                        </button>
                        <button 
                          onClick={() => openUploadModal(log, 'video')}
                          className="p-1.5 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200"
                          title="Add Video"
                        >
                          <Video size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1">
                        <button 
                          onClick={() => openUploadModal(log, 'image')}
                          className="p-1.5 bg-blue-50 text-blue-500 rounded-lg hover:bg-blue-100 flex items-center space-x-1"
                          title="Add Photo"
                        >
                          <Camera size={14} />
                        </button>
                        <button 
                          onClick={() => openUploadModal(log, 'video')}
                          className="p-1.5 bg-purple-50 text-purple-500 rounded-lg hover:bg-purple-100 flex items-center space-x-1"
                          title="Add Video"
                        >
                          <Video size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(log.status)}`}>
                      {log.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Parts Replaced Summary */}
      {logs.some(l => l.partsReplaced?.length > 0) && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Parts Replaced Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {logs.filter(l => l.partsReplaced?.length > 0).map((log) => (
              <div key={log.id} className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-mono text-[#1a237e]">{log.id}</p>
                <p className="text-sm font-medium text-gray-900 mt-1">{log.machineName}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {log.partsReplaced?.map((part, idx) => (
                    <span key={idx} className="text-xs bg-white px-2 py-1 rounded border border-gray-200">
                      {part}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && uploadingLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900">
                  Upload {uploadType === 'video' ? 'Video' : 'Photo'} Proof
                </h2>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">{uploadingLog.machineName} - {uploadingLog.id}</p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Upload Type Toggle */}
              <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setUploadType('image')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center space-x-2 transition-all ${
                    uploadType === 'image'
                      ? 'bg-white text-[#1a237e] shadow-sm'
                      : 'text-gray-600'
                  }`}
                >
                  <Camera size={16} />
                  <span>Photo</span>
                </button>
                <button
                  onClick={() => setUploadType('video')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center space-x-2 transition-all ${
                    uploadType === 'video'
                      ? 'bg-white text-[#1a237e] shadow-sm'
                      : 'text-gray-600'
                  }`}
                >
                  <Video size={16} />
                  <span>Video</span>
                </button>
              </div>

              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#1a237e] hover:bg-indigo-50/50 transition-colors"
              >
                {uploadType === 'video' ? (
                  <>
                    <Play size={40} className="mx-auto text-gray-400 mb-3" />
                    <p className="text-sm text-gray-600 font-medium">Click to select video</p>
                    <p className="text-xs text-gray-400 mt-1">MP4, WebM, MOV up to 50MB</p>
                  </>
                ) : (
                  <>
                    <Camera size={40} className="mx-auto text-gray-400 mb-3" />
                    <p className="text-sm text-gray-600 font-medium">Click to select photo</p>
                    <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP up to 50MB</p>
                  </>
                )}
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept={uploadType === 'video' ? 'video/*' : 'image/*'}
                onChange={handleFileUpload}
                className="hidden"
              />

              <p className="text-xs text-gray-500 bg-yellow-50 p-3 rounded-lg">
                <strong>Tip:</strong> Upload a {uploadType} showing the machine running after repair as proof of completion.
              </p>

              {uploading && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]"></div>
                  <span className="ml-3 text-sm text-gray-600">Uploading {uploadType}...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaintenanceLog;
